import { CollapsibleFilters } from '../common/CollapsibleFilters';
import React from "react";
import {
  Box,
  Button,
  Card,
  Chip,
  Typography,
  Input,
  FormControl,
  FormLabel,
  Select,
  Option,
  Stack,
  Alert,
} from "@mui/joy";
import type { SocialPost, SocialFilters } from "../../types/socialMedia";
import { emptySocialFilters } from "../../types/socialMedia";

export function SocialPostPlanner({
  posts,
  filters,
  onFiltersChange,
  accounts,
  onEdit,
  onApprove,
  onStats,
  renderRecovery,
}: {
  posts: SocialPost[];
  filters: SocialFilters;
  onFiltersChange: (filters: SocialFilters) => void;
  accounts: Array<{
    id: number;
    account_name: string;
    platform_display: string;
  }>;
  onEdit: (post: SocialPost) => void;
  onApprove: (post: SocialPost) => void;
  onStats: (post: SocialPost) => void;
  renderRecovery?: (post: SocialPost) => React.ReactNode;
}) {
  const update = (key: keyof SocialFilters, value: string) =>
    onFiltersChange({ ...filters, [key]: value });
  return (
    <Stack spacing={2}>
      <CollapsibleFilters><Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "2fr repeat(3, 1fr)",
          },
          gap: 1.5,
        }}
      >
        <FormControl>
          <FormLabel>Search posts</FormLabel>
          <Input
            placeholder="Search titles and captions"
            value={filters.search}
            onChange={(e) => update("search", e.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Status</FormLabel>
          <Select
            value={filters.status}
            onChange={(_, v) => update("status", v || "")}
          >
            <Option value="">All statuses</Option>
            {[
              "draft",
              "pending_review",
              "scheduled",
              "published",
              "failed",
            ].map((v) => (
              <Option key={v} value={v}>
                {v.replace("_", " ")}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Platform</FormLabel>
          <Select
            value={filters.platform}
            onChange={(_, v) => update("platform", v || "")}
          >
            <Option value="">All platforms</Option>
            <Option value="facebook">Facebook</Option>
            <Option value="instagram">Instagram</Option>
            <Option value="google_business_profile">
              Google Business Profile
            </Option>
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Account</FormLabel>
          <Select
            value={filters.account}
            onChange={(_, v) => update("account", v || "")}
          >
            <Option value="">All accounts</Option>
            {accounts.map((a) => (
              <Option key={a.id} value={String(a.id)}>
                {a.account_name} · {a.platform_display}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Post type</FormLabel>
          <Select
            value={filters.post_type}
            onChange={(_, v) => update("post_type", v || "")}
          >
            <Option value="">All types</Option>
            {[
              "general",
              "testimonial",
              "tip",
              "promotion",
              "project",
              "seasonal",
              "team",
            ].map((v) => (
              <Option key={v} value={v}>
                {v}
              </Option>
            ))}
          </Select>
        </FormControl>
        <FormControl>
          <FormLabel>Scheduled from</FormLabel>
          <Input
            type="date"
            value={filters.start_date}
            onChange={(e) => update("start_date", e.target.value)}
          />
        </FormControl>
        <FormControl>
          <FormLabel>Scheduled through</FormLabel>
          <Input
            type="date"
            value={filters.end_date}
            onChange={(e) => update("end_date", e.target.value)}
          />
        </FormControl>
        <Button
          variant="plain"
          onClick={() => onFiltersChange({ ...emptySocialFilters })}
        >
          Clear filters
        </Button>
      </Box></CollapsibleFilters>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: "repeat(2,minmax(0,1fr))",
            xl: "repeat(3,minmax(0,1fr))",
          },
          gap: 2,
        }}
      >
        {posts.map((post) => {
          const image =
            post.media_detail?.[0]?.file ||
            post.media_detail?.[0]?.thumbnail_url ||
            post.production_photos_detail?.[0]?.image_url;
          return (
            <Card key={post.id} variant="outlined" sx={{ minWidth: 0 }}>
              {image && (
                <Box
                  component="img"
                  src={image}
                  alt="Post attachment"
                  sx={{
                    width: "100%",
                    height: 160,
                    objectFit: "cover",
                    borderRadius: "sm",
                  }}
                />
              )}
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  justifyContent: "space-between",
                  alignItems: "start",
                }}
              >
                <Typography level="title-md" sx={{ overflowWrap: "anywhere" }}>
                  {post.title}
                </Typography>
                <Chip
                  size="sm"
                  color={
                    post.status === "failed"
                      ? "danger"
                      : post.status === "published"
                        ? "success"
                        : "neutral"
                  }
                >
                  {post.status_display || post.status.replace("_", " ")}
                </Chip>
              </Box>
              <Typography
                level="body-sm"
                sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {post.content}
              </Typography>
              <Typography level="body-xs">
                {post.scheduled_time
                  ? `Scheduled: ${new Date(post.scheduled_time).toLocaleString()}`
                  : "Unscheduled draft"}
              </Typography>
              <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
                {post.platforms_detail?.map((a) => (
                  <Chip
                    sx={{
                      maxWidth: "100%",
                      height: "auto",
                      "& span": {
                        whiteSpace: "normal",
                        overflowWrap: "anywhere",
                      },
                    }}
                    size="sm"
                    variant="outlined"
                    key={a.id}
                  >
                    {a.account_name} · {a.platform_display}
                  </Chip>
                ))}
              </Box>
              {post.error_message && (
                <Alert color="warning" sx={{ overflowWrap: "anywhere" }}>
                  {post.error_message}
                </Alert>
              )}
              {renderRecovery?.(post)}
              <Box
                sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: "auto" }}
              >
                {post.status !== "published" &&
                  !(post.platform_content as any)?.delivery_running &&
                  !Object.values(post.platform_content?.delivery || {}).some(
                    (value) => ["unknown", "publishing"].includes(value.status),
                  ) && (
                    <Button
                      size="sm"
                      variant="soft"
                      onClick={() => onEdit(post)}
                    >
                      Edit
                    </Button>
                  )}
                {["draft", "pending_review"].includes(post.status) && (
                  <Button size="sm" onClick={() => onApprove(post)}>
                    Approve & Schedule
                  </Button>
                )}
                {post.status === "published" && (
                  <Button
                    size="sm"
                    variant="soft"
                    onClick={() => onStats(post)}
                  >
                    View Stats
                  </Button>
                )}
              </Box>
            </Card>
          );
        })}
      </Box>
    </Stack>
  );
}
